/**
 * Sun-outage prediction, ported from loader-sunoutage.js (prediction only;
 * the 60 s monitor and its window event stay in the app). An outage is an
 * interval where the angle between the antenna-to-satellite and
 * antenna-to-sun directions drops below 1.5 times the antenna beamwidth
 * (70 lambda / D). A coarse scan finds each minimum, a 5 s scan refines it and
 * a 10 s walk measures the duration.
 */
import * as satellite from 'satellite.js';
import type { Antenna, Mission } from '@/features/loader/types';
import type { SunOutageEvent } from './types';

export interface SatrecEntry {
  id: string;
  satrec: satellite.SatRec;
  freqGHz: number;
}

export interface SunOutageInput {
  antennas: Antenna[];
  /** Missions with a TLE (GEO or otherwise); the rest are ignored. */
  missions: Mission[];
  startMs: number;
  endMs: number;
  coarseStepSec?: number;
}

const DEG = Math.PI / 180;
const AU_KM = 149597870.7;

/** Low-precision sun position (km, J2000 ECI) per the Astronomical Almanac. */
export function sunPositionEci(timeMs: number): { x: number; y: number; z: number } {
  const jd = timeMs / 86400000 + 2440587.5;
  const n = jd - 2451545.0;
  const L = (280.46 + 0.9856474 * n) % 360;
  const g = ((357.528 + 0.9856003 * n) % 360) * DEG;
  const lambda = (L + 1.915 * Math.sin(g) + 0.02 * Math.sin(2 * g)) * DEG;
  const epsilon = (23.439 - 0.0000004 * n) * DEG;
  const rAu = 1.00014 - 0.01671 * Math.cos(g) - 0.00014 * Math.cos(2 * g);
  const r = rAu * AU_KM;
  return {
    x: r * Math.cos(lambda),
    y: r * Math.cos(epsilon) * Math.sin(lambda),
    z: r * Math.sin(epsilon) * Math.sin(lambda),
  };
}

/** 3 dB beamwidth in degrees via 70 lambda / D; 5 degrees without a diameter, 12 GHz without a frequency. */
export function computeBeamwidth(diameterM: number | undefined, freqGHz?: number): number {
  if (!diameterM || diameterM <= 0) return 5;
  const f = freqGHz || 12;
  const lambdaM = 0.299792458 / f;
  return (70 * lambdaM) / diameterM;
}

/** Approximate downlink centre frequency (GHz) per band; 12 for unknown bands. */
export function bandToFreqGHz(band: string | undefined): number {
  switch (band) {
    case 'UHF':
      return 0.4;
    case 'VHF':
      return 0.15;
    case 'L-band':
      return 1.5;
    case 'S-band':
      return 2.25;
    case 'C-band':
      return 4.5;
    case 'X-band':
      return 8.5;
    case 'Ku-band':
      return 12;
    case 'K-band':
      return 22;
    case 'Ka-band':
      return 25;
    case 'EHF':
      return 40;
    case 'Optical':
      return 200000;
    default:
      return 12;
  }
}

/** Propagators for every mission with a TLE, with the band's frequency. */
export function buildSatrecEntries(missions: Mission[]): SatrecEntry[] {
  const entries: SatrecEntry[] = [];
  for (const m of missions) {
    if (!m.orbit || !m.orbit.tle) continue;
    entries.push({
      id: m.id,
      satrec: satellite.twoline2satrec(m.orbit.tle.line1, m.orbit.tle.line2),
      freqGHz: bandToFreqGHz(m.requirements && m.requirements.bandType),
    });
  }
  return entries;
}

/** Outages for every antenna against every TLE mission, antenna by antenna (each sorted by peak time). */
export function predictSunOutages(input: SunOutageInput): SunOutageEvent[] {
  const satrecs = buildSatrecEntries(input.missions);
  const events: SunOutageEvent[] = [];
  for (const antenna of input.antennas) {
    events.push(
      ...predictOutagesForAntenna(antenna, satrecs, input.startMs, input.endMs, input.coarseStepSec ?? 60),
    );
  }
  return events;
}

/** Outages of one antenna over [startMs, endMs], coarse-scanned every stepSec. */
export function predictOutagesForAntenna(
  antenna: Antenna,
  satrecs: SatrecEntry[],
  startMs: number,
  endMs: number,
  stepSec = 60,
): SunOutageEvent[] {
  if (!antenna || !antenna.location || satrecs.length === 0) return [];
  const diam = (antenna.capabilities && antenna.capabilities.diameterM) || 1;
  const antEcf = satellite.geodeticToEcf({
    longitude: antenna.location.lon * DEG,
    latitude: antenna.location.lat * DEG,
    height: 0,
  });

  const predictions: SunOutageEvent[] = [];
  const stepMs = stepSec * 1000;

  for (const entry of satrecs) {
    const beamwidth = computeBeamwidth(diam, entry.freqGHz);
    const thresholdDeg = beamwidth * 1.5;
    let prevSep: number | null = null;
    let minSep = Infinity;
    let minTime: number | null = null;

    for (let t = startMs; t <= endMs; t += stepMs) {
      const sep = separationDeg(antEcf, entry.satrec, t);
      if (sep == null) continue;

      if (sep < minSep) {
        minSep = sep;
        minTime = t;
      }
      // Crossing back above the threshold ends an outage.
      if (prevSep !== null && prevSep < thresholdDeg && sep >= thresholdDeg) {
        if (minSep <= thresholdDeg && minTime !== null) {
          const refined = refineMinimum(antEcf, entry.satrec, minTime, stepMs);
          const durationSec = outageDurationSec(antEcf, entry.satrec, refined.timeMs, thresholdDeg);
          predictions.push({
            antId: antenna.id,
            satId: entry.id,
            peakTimeMs: refined.timeMs,
            peakSeparationDeg: refined.sep,
            durationSec,
            beamwidthDeg: beamwidth,
            thresholdDeg,
          });
        }
        minSep = Infinity;
        minTime = null;
      }
      prevSep = sep;
    }
  }

  predictions.sort((a, b) => a.peakTimeMs - b.peakTimeMs);
  return predictions;
}

/** Angle (degrees) between antenna-to-satellite and antenna-to-sun at timeMs, null when SGP4 fails. */
export function separationDeg(
  antEcf: { x: number; y: number; z: number },
  satrec: satellite.SatRec,
  timeMs: number,
): number | null {
  const date = new Date(timeMs);
  const posVel = satellite.propagate(satrec, date);
  if (!posVel || !posVel.position) return null;
  const gmst = satellite.gstime(date);
  const satEcf = satellite.eciToEcf(posVel.position, gmst);

  const sx = satEcf.x - antEcf.x;
  const sy = satEcf.y - antEcf.y;
  const sz = satEcf.z - antEcf.z;
  const sMag = Math.sqrt(sx * sx + sy * sy + sz * sz);
  if (sMag === 0) return null;

  const sunEcf = satellite.eciToEcf(sunPositionEci(timeMs), gmst);
  const ux = sunEcf.x - antEcf.x;
  const uy = sunEcf.y - antEcf.y;
  const uz = sunEcf.z - antEcf.z;
  const uMag = Math.sqrt(ux * ux + uy * uy + uz * uz);
  if (uMag === 0) return null;

  let dot = (sx * ux + sy * uy + sz * uz) / (sMag * uMag);
  if (dot > 1) dot = 1;
  else if (dot < -1) dot = -1;
  return (Math.acos(dot) * 180) / Math.PI;
}

/** 5 s scan within one coarse step either side of the coarse minimum. */
function refineMinimum(
  antEcf: { x: number; y: number; z: number },
  satrec: satellite.SatRec,
  approxMs: number,
  windowMs: number,
): { timeMs: number; sep: number } {
  let bestT = approxMs;
  let bestSep = separationDeg(antEcf, satrec, approxMs) ?? Infinity;
  for (let t = approxMs - windowMs; t <= approxMs + windowMs; t += 5000) {
    const sep = separationDeg(antEcf, satrec, t);
    if (sep != null && sep < bestSep) {
      bestSep = sep;
      bestT = t;
    }
  }
  return { timeMs: bestT, sep: bestSep };
}

/** Walk outward from the peak in 10 s steps (up to 30 min each way) until the separation exceeds the threshold. */
function outageDurationSec(
  antEcf: { x: number; y: number; z: number },
  satrec: satellite.SatRec,
  peakMs: number,
  thresholdDeg: number,
): number {
  let before = peakMs;
  let after = peakMs;
  for (let dt = 10000; dt < 1800000; dt += 10000) {
    const sepB = separationDeg(antEcf, satrec, peakMs - dt);
    if (sepB != null && sepB > thresholdDeg) {
      before = peakMs - dt + 5000;
      break;
    }
  }
  for (let dt = 10000; dt < 1800000; dt += 10000) {
    const sepA = separationDeg(antEcf, satrec, peakMs + dt);
    if (sepA != null && sepA > thresholdDeg) {
      after = peakMs + dt - 5000;
      break;
    }
  }
  return Math.max(0, Math.round((after - before) / 1000));
}
